export default defineNuxtRouteMiddleware(async (to: any) => {
  const { $auth } = useNuxtApp();
  const { t } = useTexts();

  const { waitForHydration } = usePiniaHydration();

  await waitForHydration();

  const { goToLocaleRoute, getRouteBaseName, getSecureQueryParam } =
    useRoutes();

  const { setReferenceCode, setUTMParams } = useParameters();
  const { openToaster, setAuthInitialTab } = useLayoutStore();
  const { isAuthenticated } = storeToRefs(useAuthStore());
  const { isWithinMigrationPeriod } = useFeatureFlagsNew();
  const { userCanOnlyWithdraw } = useUserLimits();
  const { openLogin, openRegister } = useAuth();

  const routeName = getRouteBaseName(to);

  const isAnUnauthorizedRoute = computed<boolean>(() => {
    return $auth.isAnUnauthorizedRoute(routeName);
  });

  const isAnOnlyWithdrawRoute = computed<boolean>(() => {
    return $auth.isAnOnlyWithdrawRoute(routeName);
  });

  const isUserUnauthorizedToProceed = computed<boolean>(() => {
    return isAnUnauthorizedRoute.value && !isAuthenticated.value;
  });

  const isApiOrStoragePath =
    to.path.includes('/api/') || to.path.includes('/storage/');

  if (isApiOrStoragePath) {
    return;
  }

  if (routeName && routeName.includes('recover')) {
    return;
  }

  if (isUserUnauthorizedToProceed.value) {
    openLogin();

    return await goToLocaleRoute({ name: 'index' });
  }

  if (userCanOnlyWithdraw.value && !isAnOnlyWithdrawRoute.value) {
    if (isWithinMigrationPeriod.value) {
      return await goToLocaleRoute({ name: 'user-account' });
    } else {
      return await goToLocaleRoute({ name: 'user-wallet' });
    }
  }

  const { isOnBetBrWithoutMigrateAccepted, checkPendingContracts } =
    useUserValidations();

  if (
    isAuthenticated.value &&
    isOnBetBrWithoutMigrateAccepted.value &&
    routeName !== 'migrate-confirm' &&
    routeName !== 'page-term' &&
    routeName !== 'help'
  ) {
    return await goToLocaleRoute({ name: 'migrate-confirm' });
  }

  checkPendingContracts();

  if ((to.query.auth || to.query.login) && !isAuthenticated.value) {
    setTimeout(() => {
      openLogin();
    }, 500);
  }

  if (to.query.ref && !isAuthenticated.value) {
    if (!import.meta.client) {
      return;
    }

    const queryRef = getSecureQueryParam(to.query.ref);

    setReferenceCode(queryRef);

    clearQueryParams();

    if (!to.query.home) {
      setTimeout(() => {
        setAuthInitialTab('register');
        openRegister();
      }, 300);
    }
  }

  if (
    to.query.email &&
    to.query.service &&
    to.query.token &&
    !isAuthenticated.value
  ) {
    if (!import.meta.client) {
      return;
    }

    const { setSocialData } = useAuthSocial();

    setSocialData();

    setTimeout(() => {
      openRegister();
    }, 300);
  }

  if (to.query.src) {
    const querySrc = getSecureQueryParam(to.query.src);

    setUTMParams({ src: querySrc });
  }

  if (to.query.utm_source) {
    const queryUtmSource = getSecureQueryParam(to.query.utm_source);

    setUTMParams({ utm_source: queryUtmSource });
  }

  if (to.query.utm_campaign) {
    const queryUtmCampaign = getSecureQueryParam(to.query.utm_campaign);

    setUTMParams({ utm_campaign: queryUtmCampaign });
  }

  if (to.query.utm_content) {
    const queryUtmContent = getSecureQueryParam(to.query.utm_content);

    setUTMParams({ utm_content: queryUtmContent });
  }

  if (to.query.utm_medium) {
    const queryUtmMedium = getSecureQueryParam(to.query.utm_medium);

    setUTMParams({ utm_medium: queryUtmMedium });
  }

  if (to.query.app_source) {
    const queryAppSource = getSecureQueryParam(to.query.app_source);

    setUTMParams({ app_source: queryAppSource });
  }

  if (to.query.error) {
    setTimeout(() => {
      const queryMessage = getSecureQueryParam(to.query.message || '');

      const errorMessage = queryMessage
        ? decodeURIComponent(queryMessage)
        : t('general.error');

      openToaster({
        message: errorMessage,
        type: 'error',
        time: 8000,
      });
    }, 500);
  }
});
